import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { Button, Card, Field, Input, Notice, fmtDateTime } from '@rydres/ui';
import { driverApi } from '../lib/api.js';
import { useProfile } from '../lib/profile.js';
import type { Block, CalendarImport } from '../lib/types.js';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
type Day = { from: string; to: string } | null;

const nowLocal = (offsetH = 0) => {
  const d = new Date(Date.now() + offsetH * 3_600_000);
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Weekly hours, days off, and the calendar feed. */
export function AvailabilitySettings() {
  const navigate = useNavigate();
  const { profile, save } = useProfile();
  const [hours, setHours] = useState<Day[] | null>(null);
  const [anyTime, setAnyTime] = useState(true);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [block, setBlock] = useState({ startsAt: nowLocal(24), endsAt: nowLocal(48), reason: '' });
  const [cal, setCal] = useState<{ url: string; webcalUrl: string } | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setAnyTime(!profile.hours);
    setHours(profile.hours ?? Array.from({ length: 7 }, () => ({ from: '06:00', to: '22:00' })));
  }, [profile]);
  useEffect(() => {
    driverApi.blocks().then((r) => setBlocks(r.blocks), () => undefined);
    driverApi.calendar().then(setCal, () => undefined);
  }, []);

  const saveHours = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      await save({ hours: anyTime ? null : hours });
      setMsg({ tone: 'ok', text: 'Hours saved.' });
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : 'Could not save.' });
    }
  };
  const addBlock = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      const b = await driverApi.addBlock({ startsAt: new Date(block.startsAt).toISOString(), endsAt: new Date(block.endsAt).toISOString(), reason: block.reason || null });
      setBlocks((xs) => [...xs, b].sort((a, c) => a.startsAt.localeCompare(c.startsAt)));
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : 'Could not add that.' });
    }
  };
  const remove = async (id: string) => {
    await driverApi.removeBlock(id).catch(() => undefined);
    setBlocks((xs) => xs.filter((b) => b.id !== id));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/bookings')} className="rounded-full bg-card2 p-2 px-3" aria-label="Back">‹</button>
        <h1 className="text-xl font-black">Availability</h1>
      </div>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}

      <form onSubmit={saveHours}>
        <Card title="Hours riders can book" info={`Pickup times only, in ${profile?.timezone ?? 'your time zone'}. A window like 20:00 to 04:00 runs past midnight.`}>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={anyTime} onChange={(e) => setAnyTime(e.target.checked)} /> Any day, any time</label>
          {!anyTime && hours && (
            <div className="mt-3 space-y-2">
              {DAYS.map((name, i) => {
                const day = hours[i] ?? null;
                return (
                  <div key={name} className="flex items-center gap-2 text-sm">
                    <label className="flex w-16 items-center gap-2 font-bold"><input type="checkbox" checked={day !== null} onChange={(e) => setHours(hours.map((h, j) => (j === i ? (e.target.checked ? { from: '06:00', to: '22:00' } : null) : h)))} />{name}</label>
                    {day ? (
                      <>
                        <input type="time" value={day.from} onChange={(e) => setHours(hours.map((h, j) => (j === i ? { ...day, from: e.target.value } : h)))} className="rounded-lg bg-card2 px-2 py-1" />
                        <span className="text-muted">to</span>
                        <input type="time" value={day.to} onChange={(e) => setHours(hours.map((h, j) => (j === i ? { ...day, to: e.target.value } : h)))} className="rounded-lg bg-card2 px-2 py-1" />
                      </>
                    ) : (
                      <span className="text-muted">off</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <Button type="submit" small className="mt-3">Save hours</Button>
        </Card>
      </form>

      <Card title="Time off">
        <form onSubmit={addBlock} className="grid gap-2 sm:grid-cols-3">
          <Field label="From"><Input type="datetime-local" value={block.startsAt} onChange={(e) => setBlock({ ...block, startsAt: e.target.value })} /></Field>
          <Field label="Until"><Input type="datetime-local" value={block.endsAt} onChange={(e) => setBlock({ ...block, endsAt: e.target.value })} /></Field>
          <Field label="Note (optional)"><Input value={block.reason} onChange={(e) => setBlock({ ...block, reason: e.target.value })} placeholder="Vacation, maintenance…" /></Field>
          <div className="sm:col-span-3"><Button type="submit" small>Block this time</Button></div>
        </form>
        <ul className="mt-3 divide-y divide-line text-sm">
          {blocks.length === 0 && <li className="py-2 text-muted">No time off scheduled.</li>}
          {blocks.map((b) => (
            <li key={b.id} className="flex items-center justify-between py-2">
              <span>{fmtDateTime(b.startsAt, profile?.timezone)} → {fmtDateTime(b.endsAt, profile?.timezone)}{b.reason ? ` · ${b.reason}` : ''}</span>
              <button onClick={() => void remove(b.id)} className="text-xs font-bold text-danger">Remove</button>
            </li>
          ))}
        </ul>
      </Card>

      <Card
        title="Your calendar feed"
        info="Subscribe once and every confirmed ride and block appears in your phone's calendar. Google refreshes subscribed calendars every few hours; Apple every 15 minutes or so. Each booking screen also has an instant Add to Google Calendar link."
      >
        <p className="text-sm text-ink2">Anyone with this link can see your schedule; rotate it if it leaks.</p>
        {cal && (
          <>
            <p className="mt-2 break-all rounded-xl bg-card2 p-2 text-xs">{cal.url}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button small onClick={async () => { await navigator.clipboard?.writeText(cal.url); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? 'Copied' : 'Copy link'}</Button>
              <a className="rounded-2xl bg-card2 px-3 py-2 text-sm font-bold" href={cal.webcalUrl}>Open in Apple Calendar</a>
              <a className="rounded-2xl bg-card2 px-3 py-2 text-sm font-bold" href={`https://calendar.google.com/calendar/u/0/r/settings/addbyurl?cid=${encodeURIComponent(cal.url)}`} target="_blank" rel="noreferrer">Add to Google Calendar</a>
              <Button small variant="ghost" onClick={async () => setCal(await driverApi.rotateCalendar())}>Rotate link</Button>
            </div>
          </>
        )}
      </Card>

      <PhoneCalendars />
    </div>
  );
}

const ago = (iso: string) => {
  const min = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  return min < 1 ? 'just now' : min < 60 ? `${min} min ago` : `${Math.round(min / 60)} h ago`;
};

/** The other direction from the feed: the driver's own appointments block booking times. */
function PhoneCalendars() {
  const [list, setList] = useState<CalendarImport[] | null>(null);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [help, setHelp] = useState(false);
  useEffect(() => {
    driverApi.calendarImports().then((r) => setList(r.imports), () => setList([]));
  }, []);
  const run = async (key: string, fn: () => Promise<{ imports: CalendarImport[] }>) => {
    setBusy(key);
    setError(null);
    try {
      setList((await fn()).imports);
      if (key === 'add') setUrl('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  };
  return (
    <Card
      title="Bring in your phone calendar"
      info="Your own appointments block booking times, so no rider books you during the dentist. Riders only see that the time is taken, never what it is. Linked calendars are read every 15 minutes; events marked free do not block bookings, and your RydRes rides are left out automatically."
    >
      {list && list.length > 0 && (
        <ul className="mt-3 divide-y divide-line text-sm">
          {list.map((c) => (
            <li key={c.id} className="py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold">{c.label}</div>
                  <div className="truncate text-xs text-muted">{c.link} · {c.eventCount} {c.eventCount === 1 ? 'event' : 'events'}{c.lastOkAt ? ` · read ${ago(c.lastOkAt)}` : ''}</div>
                </div>
                <div className="flex shrink-0 gap-3 text-xs font-bold">
                  <button disabled={busy !== null} onClick={() => void run(c.id, () => driverApi.refreshCalendarImport(c.id))}>{busy === c.id ? 'Reading…' : 'Read now'}</button>
                  <button disabled={busy !== null} className="text-danger" onClick={() => void run(c.id, () => driverApi.removeCalendarImport(c.id))}>Remove</button>
                </div>
              </div>
              {c.lastError && <p className="mt-1 text-xs text-danger">Last read failed: {c.lastError}</p>}
            </li>
          ))}
        </ul>
      )}
      <form
        className="mt-3 space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          void run('add', () => driverApi.addCalendarImport({ url }));
        }}
      >
        <Field label="Private calendar link">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://calendar.google.com/calendar/ical/…" autoCapitalize="none" autoCorrect="off" inputMode="url" />
        </Field>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" small disabled={!url.trim() || busy !== null}>{busy === 'add' ? 'Reading the calendar…' : 'Link calendar'}</Button>
          <button type="button" className="text-sm font-bold text-accent-text underline" onClick={() => setHelp(!help)}>Where do I find it?</button>
        </div>
      </form>
      {error && <div className="mt-2"><Notice tone="danger">{error}</Notice></div>}
      {help && (
        <div className="mt-3 space-y-2 text-sm text-ink2">
          <p><strong>Google Calendar</strong> (on a computer): Settings → pick your calendar → Integrate calendar → copy "Secret address in iCal format".</p>
          <p><strong>iPhone</strong>: Calendar app → Calendars → ⓘ next to the calendar → turn on Public Calendar → Share Link. Anyone who has that link can read the calendar, so keep it to yourself.</p>
          <p><strong>Outlook</strong>: Settings → Calendar → Shared calendars → Publish a calendar → copy the ICS link.</p>
        </div>
      )}
    </Card>
  );
}
